import { useEffect, useRef, useState } from 'react';
import { Badge, Box, Chip, Drawer, Group, Stack, Text, Textarea } from '@mantine/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { SPEAKER_TAGS, speakerKeys, type Round, type Segment, type SpeakerNote } from '@flow/core';
import { run, useDeps, useUseCases } from '../../app/services';
import { useSpeakers, type Speaker } from './useSpeakers';

const EMPTY: SpeakerNote = { text: '', tags: [] };

/** One debater's notes: quick tags plus free text. Saves as you go. */
const SpeakerCard = ({ round, s, note, current, autoFocus }: { round: Round; s: Speaker; note: SpeakerNote; current: boolean; autoFocus: boolean }) => {
  const uc = useUseCases();
  const [text, setText] = useState(note.text);
  const saved = useRef(note.text);
  const tagsRef = useRef(note.tags);
  tagsRef.current = note.tags;
  const save = (patch: Partial<SpeakerNote>) => run(uc.saveSpeakerNote(round.id, s.key, { text: saved.current, tags: tagsRef.current, ...patch }));

  // Save text shortly after typing stops, and when leaving the box.
  useEffect(() => {
    if (text === saved.current) return;
    const id = setTimeout(() => { saved.current = text; void save({ text }); }, 500);
    return () => clearTimeout(id);
  }, [text]);
  const flush = () => { if (text !== saved.current) { saved.current = text; void save({ text }); } };

  const sideLabel = s.side === null ? null : round.format.sideLabels[s.side];
  return (
    <Box p="xs" style={{ borderRadius: 6, border: `1px solid ${current ? 'var(--mantine-primary-color-filled)' : 'var(--grid-line)'}`, background: current ? 'var(--mantine-primary-color-light)' : undefined }} data-testid={`speaker-${s.key}`} data-current={current ? '1' : '0'}>
      <Group gap={6} mb={6} wrap="nowrap">
        <Text size="sm" fw={700} lineClamp={1} style={{ color: s.side === null ? undefined : `var(--side-${s.side})` }}>{s.label}</Text>
        <Text size="xs" c="dimmed" lineClamp={1}>{s.code}{sideLabel ? ` · ${sideLabel}` : ''}</Text>
        {current && <Badge size="xs" variant="filled">speaking</Badge>}
      </Group>
      <Chip.Group multiple value={note.tags} onChange={(tags) => void save({ tags })}>
        <Group gap={4} mb={4}>
          {SPEAKER_TAGS.strengths.map((t) => <Chip key={t} value={t} size="xs" color="green" variant="light">{t}</Chip>)}
        </Group>
        <Group gap={4} mb={6}>
          {SPEAKER_TAGS.toWorkOn.map((t) => <Chip key={t} value={t} size="xs" color="orange" variant="light">{t}</Chip>)}
        </Group>
      </Chip.Group>
      <Textarea classNames={{ input: 'writing' }}
        autosize
        minRows={2}
        maxRows={8}
        placeholder="Notes for points and comments (private)"
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        onBlur={flush}
        data-autofocus={autoFocus || undefined}
        aria-label={`Notes on ${s.label}`}
      />
    </Box>
  );
};

/** Notes on the round as a whole: atmosphere, rulings, anything not about one debater. Saves as you go. */
const RoundNotesCard = ({ round, initial }: { round: Round; initial: string }) => {
  const uc = useUseCases();
  const [text, setText] = useState(initial);
  const saved = useRef(initial);
  const flush = (value = text) => {
    if (value === saved.current) return;
    saved.current = value;
    void run(uc.saveBallot(round.id, { roundNotes: value }));
  };
  useEffect(() => {
    const id = setTimeout(() => flush(text), 500);
    return () => clearTimeout(id);
  }, [text]);
  return (
    <Box p="xs" style={{ borderRadius: 6, border: '1px solid var(--grid-line)' }}>
      <Text size="sm" fw={700} mb={6}>About the round</Text>
      <Textarea classNames={{ input: 'writing' }}
        autosize
        minRows={2}
        maxRows={8}
        placeholder="Anything not about one debater: timing issues, evidence questions, what to say in the RFD…"
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        onBlur={() => flush()}
        aria-label="Notes about the round"
      />
    </Box>
  );
};

/**
 * Side drawer for notes on each debater, openable mid-round without covering the whole flow (⌥N).
 * Whoever is speaking now (per the timer) is highlighted and focused.
 */
export const SpeakerNotesDrawer = ({ round, segment, opened, onClose }: { round: Round; segment: Segment | null; opened: boolean; onClose: () => void }) => {
  const deps = useDeps();
  const speakers = useSpeakers(round);
  // null = no ballot yet, undefined = still loading. Cards render once it's known and are keyed by speaker only, so
  // the ballot being created by the first note doesn't remount them (and wipe what's being typed).
  const ballot = useLiveQuery(async () => (await deps.ballots.get(round.id)) ?? null, [round.id]);
  const current = new Set(speakerKeys(round, segment));
  const firstCurrent = speakers.find((s) => current.has(s.key))?.key ?? speakers[0]?.key;
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={380}
      title={<Text fw={600}>Notes</Text>}
      withOverlay={false}
      lockScroll={false}
      closeOnClickOutside={false}
      zIndex={350}
      shadow="xl"
    >
      <Stack gap="sm" data-testid="speaker-notes">
        {ballot !== undefined && <RoundNotesCard round={round} initial={ballot?.roundNotes ?? ''} />}
        {round.teamASide === null && <Text size="xs" c="dimmed">Set sides to see who's speaking.</Text>}
        {ballot !== undefined && speakers.map((s) => (
          <SpeakerCard key={s.key} round={round} s={s} note={ballot?.speakerNotes?.[s.key] ?? EMPTY} current={current.has(s.key)} autoFocus={s.key === firstCurrent} />
        ))}
        <Text size="xs" c="dimmed">Private: these stay on this device and never go into your ballot unless you copy them in (Ballot tab).</Text>
      </Stack>
    </Drawer>
  );
};
